import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  Download,
  FileCode2,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react';
import { ApiError } from '../lib/api';
import {
  fetchIssuedInvoiceFile,
  type IssuedInvoice,
  type IssuedInvoiceCustomerInput,
  type IssuedInvoiceInput,
  type IssuedInvoiceLineInput,
} from '../lib/issuedInvoicesApi';
import { computeTotals } from '../lib/invoiceMath';
import {
  useApproveIssuedInvoice,
  useCancelIssuedInvoice,
  useCreateIssuedInvoice,
  useCreateIssuedInvoiceStorno,
  useDuplicateIssuedInvoice,
  useSetIssuedInvoiceProject,
  useInvoiceSeries,
  useIssuedInvoice,
  useRefreshIssuedInvoiceState,
  useUpdateIssuedInvoice,
  useValidateIssuedInvoice,
} from '../hooks/useIssuedInvoices';
import { useCompanies } from '../hooks/useCompanies';
import { useBankAccounts } from '../hooks/useBankAccounts';
import { useUsers } from '../hooks/useUsers';
import { useProjects } from '../hooks/useProjects';
import { useAuthStore } from '../store/useAuthStore';
import { useActiveCompanyId } from '../store/useCompanyStore';
import { usePermissions } from '../hooks/usePermissions';
import { InvoiceSeriesModal } from '../components/domain/issuedInvoices/InvoiceSeriesModal';
import { useConfirm } from '../components/ui/ConfirmProvider';
import { DateField } from '../components/ui/DateField';
import { Select } from '../components/ui/Select';
import { CustomerPicker, EMPTY_CUSTOMER } from '../components/domain/issuedInvoices/CustomerPicker';
import {
  IssuedInvoiceLinesEditor,
  emptyLine,
} from '../components/domain/issuedInvoices/IssuedInvoiceLinesEditor';
import {
  ISSUED_STATUS_LABELS,
  IssuedInvoiceStatusBadge,
} from '../components/domain/issuedInvoices/IssuedInvoiceStatusBadge';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function money(v: number): string {
  return new Intl.NumberFormat('ro-RO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
}

function roDateTime(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' });
}

interface FormState {
  seriesId: string;
  issueDate: string;
  dueDate: string;
  pricesIncludeVat: boolean;
  notes: string;
  paymentIban: string;
  paymentBank: string;
  preparedBy: string;
  showPreparedBy: boolean;
  /** Internal project tag ('' = none). Never printed, never sent to ANAF. */
  projectId: string;
  customer: IssuedInvoiceCustomerInput;
  lines: IssuedInvoiceLineInput[];
}

function initialForm(): FormState {
  const issue = today();
  return {
    seriesId: '',
    issueDate: issue,
    dueDate: addDays(issue, 30),
    pricesIncludeVat: true,
    notes: '',
    paymentIban: '',
    paymentBank: '',
    preparedBy: '',
    showPreparedBy: true,
    projectId: '',
    customer: { ...EMPTY_CUSTOMER },
    lines: [emptyLine()],
  };
}

function formFromInvoice(inv: IssuedInvoice): FormState {
  return {
    seriesId: inv.seriesId ?? '',
    issueDate: inv.issueDate,
    dueDate: inv.dueDate,
    pricesIncludeVat: inv.pricesIncludeVat,
    notes: inv.notes ?? '',
    paymentIban: inv.paymentIban ?? '',
    paymentBank: inv.paymentBank ?? '',
    preparedBy: inv.preparedBy ?? '',
    showPreparedBy: inv.showPreparedBy,
    projectId: inv.projectId ?? '',
    customer: {
      clientId: inv.customer.clientId,
      name: inv.customer.name,
      cui: inv.customer.cui,
      regCom: inv.customer.regCom ?? '',
      vatPayer: inv.customer.vatPayer,
      street: inv.customer.street,
      city: inv.customer.city,
      countyCode: inv.customer.countyCode,
      country: inv.customer.country,
      email: inv.customer.email ?? '',
    },
    lines: inv.lines.map((l) => ({
      name: l.name,
      description: l.description ?? '',
      unit: l.unitCode,
      quantity: l.quantity,
      unitPrice: l.unitPrice,
      vatRate: l.vatRate,
    })),
  };
}

const EDITABLE = new Set(['DRAFT', 'REJECTED', 'FAILED']);

/**
 * SmartBill-style invoice editor. `/financials/issued/new` creates a draft;
 * `/financials/issued/:id` edits it while it is still a draft (or rejected /
 * failed), and otherwise shows the read-only document with its ANAF state.
 */
export function IssuedInvoiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { can } = usePermissions();
  const canManageSeries = can('financials', 'canApproveInvoices');
  const [showSeries, setShowSeries] = useState(false);
  const canIssue = can('financials', 'canIssueInvoices');
  const canApprove = can('financials', 'canApproveInvoices');

  const activeCompanyId = useActiveCompanyId();
  const companies = useCompanies();
  const invoiceQuery = useIssuedInvoice(isNew ? null : (id ?? null));
  const invoice = invoiceQuery.data ?? null;

  const companyId =
    invoice?.companyId ?? activeCompanyId ?? companies.data?.find((c) => c.isDefault)?.id;
  const company =
    companies.data?.find((c) => c.id === companyId) ??
    companies.data?.find((c) => c.isDefault) ??
    null;
  const sellerVatPayer = company?.vatPayer ?? true;
  const series = useInvoiceSeries(companyId ?? undefined);
  const bankAccounts = useBankAccounts(companyId ?? undefined);
  const users = useUsers();
  // Every firm's projects: the tag says what the invoice is FOR, and any firm
  // may bill any project (`companyId: undefined` overrides the firm switcher).
  const projects = useProjects({ companyId: undefined, limit: 200 });
  const currentUserName = useAuthStore((s) => s.user?.name ?? '');

  const [form, setForm] = useState<FormState>(initialForm);
  const [dirty, setDirty] = useState(false);
  const [message, setMessage] = useState<{
    kind: 'ok' | 'error' | 'info';
    text: string;
    details?: string[];
  } | null>(null);
  const messageRef = useRef<HTMLDivElement>(null);
  // The last ANAF check, shown on the button itself so nobody has to scroll
  // up to the message. Any edit makes it stale, so patch() clears it.
  const [validation, setValidation] = useState<'ok' | 'failed' | null>(null);

  useEffect(() => {
    if (invoice && !dirty) setForm(formFromInvoice(invoice));
  }, [invoice, dirty]);

  useEffect(() => {
    if (isNew && !form.seriesId && series.data?.length) {
      const def = series.data.find((s) => s.isDefault) ?? series.data[0];
      setForm((f) => ({ ...f, seriesId: def.id }));
    }
  }, [isNew, form.seriesId, series.data]);

  const create = useCreateIssuedInvoice();
  const update = useUpdateIssuedInvoice();
  const validate = useValidateIssuedInvoice();
  const approve = useApproveIssuedInvoice();
  const refresh = useRefreshIssuedInvoiceState();
  const cancel = useCancelIssuedInvoice();
  const duplicate = useDuplicateIssuedInvoice();
  const storno = useCreateIssuedInvoiceStorno();
  const setProject = useSetIssuedInvoiceProject();

  // A storno reverses an invoice ANAF already holds: same lines, negative
  // quantities. Everything else about it — numbering, validation, approval —
  // is an ordinary invoice's path.
  const isStorno = Boolean(invoice?.stornoOfId);
  const activeStorno = (invoice?.stornos ?? []).find((s) => s.status !== 'CANCELLED') ?? null;
  const canStorno = Boolean(
    invoice && !isStorno && !activeStorno && ['SENT', 'ACCEPTED'].includes(invoice.status),
  );
  const editable = isNew || (invoice ? EDITABLE.has(invoice.status) : false);
  const readOnly = !editable || !canIssue;
  const busy = create.isPending || update.isPending || approve.isPending;

  const totals = useMemo(
    () =>
      computeTotals(
        form.lines.map((l) => ({
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          vatRate: sellerVatPayer ? l.vatRate : 0,
          vatCategory: sellerVatPayer ? undefined : 'O',
        })),
        form.pricesIncludeVat,
      ),
    [form.lines, form.pricesIncludeVat, sellerVatPayer],
  );

  const ibanOptions = useMemo(() => {
    const out: Array<{ value: string; label: string; bank: string }> = [];
    const seen = new Set<string>();
    const add = (iban: string | null | undefined, bank: string, label?: string | null) => {
      const clean = (iban ?? '').replace(/\s+/g, '').toUpperCase();
      if (!clean || seen.has(clean)) return;
      seen.add(clean);
      out.push({ value: clean, bank, label: `${clean} · ${label || bank}` });
    };
    if (company?.iban) add(company.iban, company.bank ?? '', 'IBAN-ul firmei');
    for (const acc of bankAccounts.data ?? []) {
      if (acc.isActive) add(acc.iban, acc.bankName, acc.label);
    }
    if (form.paymentIban) add(form.paymentIban, form.paymentBank, null);
    return out;
  }, [company?.iban, company?.bank, bankAccounts.data, form.paymentIban, form.paymentBank]);
  const preparedByOptions = useMemo(() => {
    const names = new Set<string>((users.data ?? []).map((u) => u.name).filter(Boolean));
    if (form.preparedBy) names.add(form.preparedBy);
    return [...names].sort().map((n) => ({ value: n, label: n }));
  }, [users.data, form.preparedBy]);

  const projectOptions = useMemo(() => {
    const out = new Map<string, string>();
    for (const p of projects.data?.projects ?? []) {
      out.set(p.id, p.code ? `${p.name} (${p.code})` : p.name);
    }
    // The tagged project may be outside the list (archived, out of scope).
    if (invoice?.project && !out.has(invoice.project.id)) {
      const p = invoice.project;
      out.set(p.id, p.code ? `${p.name} (${p.code})` : p.name);
    }
    return [...out.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label, 'ro'));
  }, [projects.data, invoice?.project]);

  function patch(p: Partial<FormState>) {
    setDirty(true);
    setValidation(null);
    setForm((f) => ({ ...f, ...p }));
  }

  // The project tag is internal, so it may change on a sent invoice too; there
  // it is saved on the spot (the rest of the form is locked).
  function changeProject(value: string) {
    if (editable || !invoice) {
      patch({ projectId: value });
      return;
    }
    setForm((f) => ({ ...f, projectId: value }));
    setProject.mutate(
      { id: invoice.id, projectId: value || null },
      {
        onSuccess: () => setMessage({ kind: 'ok', text: 'Proiectul a fost salvat.' }),
        onError: (err) => showError(err, 'Nu am putut salva proiectul.'),
      },
    );
  }

  function toInput(): IssuedInvoiceInput {
    return {
      companyId: companyId ?? undefined,
      seriesId: form.seriesId || null,
      issueDate: form.issueDate,
      dueDate: form.dueDate,
      pricesIncludeVat: form.pricesIncludeVat,
      notes: form.notes.trim() || null,
      paymentIban: form.paymentIban.trim() || null,
      paymentBank: form.paymentBank.trim() || null,
      preparedBy: form.preparedBy.trim() || null,
      showPreparedBy: form.showPreparedBy,
      projectId: form.projectId || null,
      customer: {
        ...form.customer,
        regCom: form.customer.regCom?.trim() || null,
        email: form.customer.email?.trim() || null,
        country: 'RO',
      },
      lines: form.lines.map((l) => ({ ...l, description: l.description?.trim() || null })),
    };
  }

  function validateLocally(): string | null {
    if (!form.customer.cui.trim()) return 'Completează CUI-ul clientului.';
    if (!form.customer.name.trim()) return 'Completează denumirea clientului.';
    if (!form.customer.street.trim()) return 'Completează strada clientului.';
    if (!form.customer.countyCode) return 'Alege județul clientului.';
    if (!form.customer.city.trim()) return 'Completează localitatea clientului.';
    if (form.dueDate < form.issueDate)
      return 'Termenul de plată nu poate fi înaintea datei emiterii.';
    if (form.lines.some((l) => !l.name.trim())) return 'Fiecare linie are nevoie de o denumire.';
    if (isStorno) {
      if (form.lines.some((l) => !(l.quantity < 0)))
        return 'Pe o factură storno toate cantitățile trebuie să fie negative.';
    } else if (form.lines.some((l) => !(l.quantity > 0))) {
      return 'Cantitatea trebuie să fie mai mare decât 0.';
    }
    return null;
  }

  function showError(err: unknown, fallback: string) {
    setMessage({ kind: 'error', text: err instanceof ApiError ? err.message : fallback });
  }

  async function save(): Promise<IssuedInvoice | null> {
    const problem = validateLocally();
    if (problem) {
      setMessage({ kind: 'error', text: problem });
      return null;
    }
    setMessage(null);
    try {
      const saved = isNew
        ? await create.mutateAsync(toInput())
        : await update.mutateAsync({ id: invoice!.id, input: toInput() });
      setDirty(false);
      setMessage({ kind: 'ok', text: 'Ciorna a fost salvată.' });
      if (isNew) navigate(`/financials/issued/${saved.id}`, { replace: true });
      return saved;
    } catch (err) {
      showError(err, 'Salvarea a eșuat.');
      return null;
    }
  }

  async function handleValidate() {
    const saved = dirty || isNew ? await save() : invoice;
    if (!saved) return;
    try {
      const result = await validate.mutateAsync(saved.id);
      setValidation(result.ok ? 'ok' : 'failed');
      // The problems are listed in the message up top: bring it into view.
      if (!result.ok) {
        requestAnimationFrame(() =>
          messageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
        );
      }
      setMessage(
        result.ok
          ? { kind: 'ok', text: 'ANAF a validat XML-ul facturii. Poate fi aprobată.' }
          : { kind: 'error', text: 'ANAF a găsit probleme în factură:', details: result.messages },
      );
    } catch (err) {
      setValidation(null);
      showError(err, 'Validarea la ANAF a eșuat.');
    }
  }

  async function handleApprove() {
    const saved = dirty || isNew ? await save() : invoice;
    if (!saved) return;
    const nextNumber = series.data?.find(
      (s) => s.id === (saved.seriesId ?? form.seriesId),
    )?.nextFullNumber;
    const ok = await confirm({
      title: 'Aprobi și trimiți factura în SPV?',
      message:
        `${saved.fullNumber ?? nextNumber ?? 'Factura'} către ${saved.customer.name} — ${money(saved.totalAmount)} ${saved.currency}. ` +
        'Factura primește număr, devine definitivă și pleacă imediat la ANAF. Nu se mai poate modifica.',
      confirmLabel: 'Aprobă și trimite',
    });
    if (!ok) return;
    setMessage({ kind: 'info', text: 'Se validează și se trimite la ANAF…' });
    try {
      const result = await approve.mutateAsync(saved.id);
      setDirty(false);
      setMessage({
        kind: 'ok',
        text: `Factura ${result.invoice.fullNumber} a fost trimisă în SPV (index ${result.invoice.anaf.indexIncarcare}). Starea se actualizează automat.`,
      });
    } catch (err) {
      showError(err, 'Trimiterea în SPV a eșuat.');
    }
  }

  async function handleCancel() {
    if (!invoice) return;
    const ok = await confirm('Anulezi această ciornă? Nu poate fi recuperată.');
    if (!ok) return;
    try {
      await cancel.mutateAsync(invoice.id);
      navigate('/invoices');
    } catch (err) {
      showError(err, 'Anularea a eșuat.');
    }
  }

  async function handleDuplicate() {
    if (!invoice) return;
    try {
      const copy = await duplicate.mutateAsync(invoice.id);
      setDirty(false);
      navigate(`/financials/issued/${copy.id}`);
    } catch (err) {
      showError(err, 'Duplicarea a eșuat.');
    }
  }

  /**
   * The same act as „Emite storno” in Facturi: write the reversing draft,
   * validated at ANAF, and open it in its own tab — this page keeps showing
   * the invoice that was reversed, which is what the reader was looking at.
   */
  async function handleStorno() {
    if (!invoice) return;
    const yes = await confirm({
      title: 'Emiți storno pentru această factură?',
      message:
        `Se pregătește o factură storno pentru ${invoice.fullNumber ?? 'această factură'} — aceleași linii, în minus. ` +
        'Nu pleacă nimic la ANAF acum: o verifici în fila care se deschide și o emiți de acolo.',
      confirmLabel: 'Emite storno',
    });
    if (!yes) return;
    try {
      const result = await storno.mutateAsync(invoice.id);
      window.open(`/financials/issued/${result.invoice.id}`, '_blank', 'noopener');
      setMessage(
        result.validation === null
          ? {
              kind: 'info',
              text: 'Ciorna de storno e gata, dar validatorul ANAF nu a răspuns. Valideaz-o din fila deschisă înainte să o emiți.',
            }
          : result.validation.ok
            ? {
                kind: 'ok',
                text: 'Ciorna de storno e gata și a trecut validarea ANAF. Verific-o în fila deschisă și emite-o de acolo.',
              }
            : {
                kind: 'error',
                text: 'Ciorna de storno e gata, dar ANAF a găsit probleme:',
                details: result.validation.messages,
              },
      );
    } catch (err) {
      showError(err, 'Factura storno nu a putut fi pregătită.');
    }
  }

  async function openFile(kind: 'pdf' | 'xml') {
    if (!invoice) return;
    try {
      const { url, fileName } = await fetchIssuedInvoiceFile(invoice.id, kind);
      // Saved under the server's name („Factură Clean Code Culture - FIRMIO
      // 0001.pdf”); a blob opened in a tab would be saved as a random id.
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      showError(err, 'Descărcarea a eșuat.');
    }
  }

  if (!isNew && invoiceQuery.isLoading) {
    return <div className="p-8 text-sm text-gray-500">Se încarcă factura…</div>;
  }
  if (!isNew && invoiceQuery.isError) {
    return (
      <div className="p-8 text-sm text-red-600">
        Factura nu a putut fi încărcată.{' '}
        <Link to="/invoices" className="text-blue-600 hover:underline">
          Înapoi la Facturi
        </Link>
      </div>
    );
  }

  const seriesOptions = (series.data ?? []).map((s) => ({
    value: s.id,
    label: `${s.prefix} · următorul ${s.nextFullNumber}`,
  }));
  const selectedSeries = series.data?.find((s) => s.id === form.seriesId);
  const numberLabel =
    invoice?.fullNumber ??
    (selectedSeries ? `${selectedSeries.nextFullNumber} (la aprobare)` : '—');

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Link
            to="/invoices"
            className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800"
          >
            <ArrowLeft className="h-4 w-4" /> Facturi
          </Link>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 text-2xl font-bold text-gray-900">
            {isNew ? 'Factură nouă' : (invoice?.fullNumber ?? 'Ciornă de factură')}
            {invoice && <IssuedInvoiceStatusBadge status={invoice.status} />}
          </h1>
          <p className="text-sm text-gray-500">
            Emitent: <span className="font-medium text-gray-700">{company?.legalName ?? '…'}</span>
            {company && !company.vatPayer && ' · neplătitor de TVA'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {invoice && (
            <>
              <button
                type="button"
                onClick={() => void openFile('pdf')}
                className="btn-secondary inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                <Download className="h-4 w-4" /> PDF
              </button>
              <button
                type="button"
                onClick={() => void openFile('xml')}
                className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                <FileCode2 className="h-4 w-4" /> XML
              </button>
              {canIssue && (
                <button
                  type="button"
                  onClick={() => void handleDuplicate()}
                  className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50"
                >
                  Duplică
                </button>
              )}
              {canIssue && canStorno && (
                <button
                  type="button"
                  disabled={storno.isPending}
                  onClick={() => void handleStorno()}
                  title="Pregătește factura storno (aceleași linii, în minus) și deschide-o într-o filă nouă"
                  className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                >
                  {storno.isPending ? 'Se pregătește…' : 'Emite storno'}
                </button>
              )}
            </>
          )}
          {invoice && (invoice.status === 'SENT' || invoice.status === 'SENDING') && canApprove && (
            <button
              type="button"
              onClick={() =>
                refresh.mutate(invoice.id, { onError: (e) => showError(e, 'Verificarea a eșuat.') })
              }
              disabled={refresh.isPending}
              className="inline-flex items-center gap-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${refresh.isPending ? 'animate-spin' : ''}`} />{' '}
              Verifică starea
            </button>
          )}
        </div>
      </div>

      {message && (
        <div
          ref={messageRef}
          className={`mt-4 rounded border px-3 py-2 text-sm ${
            message.kind === 'ok'
              ? 'border-green-200 bg-green-50 text-green-800'
              : message.kind === 'error'
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-blue-200 bg-blue-50 text-blue-800'
          }`}
        >
          <div>{message.text}</div>
          {message.details && message.details.length > 0 && (
            <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs">
              {message.details.map((d, i) => (
                <li key={i}>{d}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {invoice?.stornoOf && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">Factură storno.</span> Anulează factura{' '}
          <Link
            to={`/financials/issued/${invoice.stornoOf.id}`}
            className="font-medium underline hover:no-underline"
          >
            {invoice.stornoOf.fullNumber ?? 'fără număr'}
          </Link>{' '}
          din {invoice.stornoOf.issueDate.split('-').reverse().join('.')} (
          {money(invoice.stornoOf.totalAmount)} {invoice.currency}). Liniile sunt în minus; verifică
          sumele și, când e în regulă, apasă „Aprobă și trimite” — abia atunci primește număr și
          pleacă la ANAF.
        </div>
      )}

      {activeStorno && (
        <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Factura aceasta este stornată prin{' '}
          <Link
            to={`/financials/issued/${activeStorno.id}`}
            className="font-medium underline hover:no-underline"
          >
            {activeStorno.fullNumber ?? 'o ciornă de storno, neaprobată încă'}
          </Link>
          .
        </div>
      )}

      {invoice && invoice.status !== 'DRAFT' && invoice.status !== 'CANCELLED' && (
        <div className="mt-4 rounded-lg border border-gray-200 bg-white p-4 text-sm">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            <span className="font-medium text-gray-900">ANAF SPV</span>
            <span className="text-gray-600">
              Stare: {invoice.anaf.state ?? ISSUED_STATUS_LABELS[invoice.status]}
            </span>
            <span className="text-gray-600">
              Index încărcare: {invoice.anaf.indexIncarcare ?? '—'}
            </span>
            <span className="text-gray-600">Trimisă: {roDateTime(invoice.anaf.sentAt)}</span>
            {invoice.anaf.acceptedAt && (
              <span className="inline-flex items-center gap-1 text-green-700">
                <CheckCircle2 className="h-4 w-4" /> Acceptată:{' '}
                {roDateTime(invoice.anaf.acceptedAt)}
              </span>
            )}
            {invoice.anaf.lastCheckedAt && (
              <span className="text-gray-500">
                Verificat: {roDateTime(invoice.anaf.lastCheckedAt)}
              </span>
            )}
          </div>
          {invoice.anaf.messages.length > 0 && (
            <ul className="mt-2 list-disc space-y-0.5 pl-5 text-xs text-red-700">
              {invoice.anaf.messages.map((m, i) => (
                <li key={i}>{m}</li>
              ))}
            </ul>
          )}
          {(invoice.status === 'REJECTED' || invoice.status === 'FAILED') && canIssue && (
            <p className="mt-2 text-xs text-gray-600">
              Corectează datele de mai jos și apasă „Aprobă și retrimite”. Numărul{' '}
              {invoice.fullNumber} rămâne același.
            </p>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-3">
        <section className="rounded-lg border border-gray-200 bg-white p-4 lg:col-span-2">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Client
          </h2>
          <CustomerPicker
            value={form.customer}
            onChange={(customer) => patch({ customer })}
            disabled={readOnly}
          />
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
            Document
          </h2>
          <div className="space-y-3">
            <div>
              <Select
                label="Serie"
                value={form.seriesId}
                onChange={(v) => patch({ seriesId: v })}
                options={seriesOptions}
                disabled={readOnly || Boolean(invoice?.number)}
                loading={series.isLoading}
                width={260}
                className="w-full"
              />
              {canManageSeries && !invoice?.number && (
                <button
                  type="button"
                  onClick={() => setShowSeries(true)}
                  className="mt-1 text-xs text-brand-700 hover:underline"
                >
                  Administrează seriile
                </button>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600">Număr</div>
              <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                {numberLabel}
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">Data emiterii</label>
              <DateField
                value={form.issueDate}
                onChange={(v) => {
                  const due = form.dueDate < v ? addDays(v, 30) : form.dueDate;
                  patch({ issueDate: v, dueDate: due });
                }}
                required
                disabled={readOnly}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600">
                Termen de plată
              </label>
              <DateField
                value={form.dueDate}
                onChange={(v) => patch({ dueDate: v })}
                min={form.issueDate}
                required
                disabled={readOnly}
              />
              {!readOnly && (
                <div className="mt-1 flex gap-2 text-xs">
                  {[0, 15, 30].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => patch({ dueDate: addDays(form.issueDate, d) })}
                      className="rounded border border-gray-200 px-2 py-0.5 text-gray-600 hover:bg-gray-50"
                    >
                      {d === 0 ? 'la emitere' : `+${d} zile`}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-medium text-gray-600">Monedă</div>
              <div className="mt-1 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                RON
              </div>
            </div>
            <div>
              <Select
                label="Proiect (doar intern)"
                value={form.projectId}
                onChange={changeProject}
                options={projectOptions}
                placeholder="Fără proiect"
                clearable
                searchable
                disabled={!canIssue || setProject.isPending}
                loading={projects.isLoading}
                width={260}
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Etichetă pentru Firmio: factura apare în tabul Financiar al proiectului, oricare ar
                fi firma care o emite. Nu apare pe PDF și nu pleacă la ANAF.
              </p>
            </div>
            <div>
              <Select
                label="IBAN pe factură"
                value={form.paymentIban}
                onChange={(v) => {
                  const acc = ibanOptions.find((o) => o.value === v);
                  patch({ paymentIban: v, paymentBank: acc?.bank ?? '' });
                }}
                options={ibanOptions.map((o) => ({ value: o.value, label: o.label }))}
                placeholder="IBAN-ul firmei"
                clearable
                disabled={readOnly}
                loading={bankAccounts.isLoading}
                width={260}
                className="w-full"
              />
              <p className="mt-1 text-xs text-gray-500">
                Gol = IBAN-ul din profilul firmei. Conturile vin din Financiar › Conturi bancare.
              </p>
            </div>
            <div>
              <Select
                label="Întocmit de"
                value={form.preparedBy}
                onChange={(v) => patch({ preparedBy: v })}
                options={preparedByOptions}
                placeholder={currentUserName || 'Cine aprobă'}
                clearable
                searchable
                disabled={readOnly}
                loading={users.isLoading}
                width={260}
                className="w-full"
              />
              <label className="mt-2 inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={form.showPreparedBy}
                  disabled={readOnly}
                  onChange={(e) => patch({ showPreparedBy: e.target.checked })}
                />
                Afișează „Întocmit de” pe factură
              </label>
            </div>
            {sellerVatPayer && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300"
                  checked={form.pricesIncludeVat}
                  disabled={readOnly}
                  onChange={(e) => patch({ pricesIncludeVat: e.target.checked })}
                />
                Prețurile includ TVA
              </label>
            )}
          </div>
        </section>
      </div>

      <section className="mt-4 rounded-lg border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
          Produse și servicii
        </h2>
        <IssuedInvoiceLinesEditor
          lines={form.lines}
          pricesIncludeVat={form.pricesIncludeVat}
          sellerVatPayer={sellerVatPayer}
          onChange={(lines) => patch({ lines })}
          disabled={readOnly}
          allowNegativeQuantity={isStorno}
        />
        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1">
            <label className="mb-1 block text-xs font-medium text-gray-600">
              Mențiuni (apar pe factură)
            </label>
            <textarea
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50"
              rows={3}
              maxLength={300}
              value={form.notes}
              disabled={readOnly}
              onChange={(e) => patch({ notes: e.target.value })}
            />
          </div>
          <div className="w-full rounded border border-gray-200 bg-gray-50 p-3 text-sm sm:w-72">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal (fără TVA)</span>
              <span className="tabular-nums">{money(totals.netAmount)} lei</span>
            </div>
            {totals.breakdown.map((b) => (
              <div
                key={`${b.vatCategory}-${b.vatRate}`}
                className="flex justify-between text-gray-600"
              >
                <span>{b.vatCategory === 'S' ? `TVA ${b.vatRate}%` : 'TVA (neimpozabil)'}</span>
                <span className="tabular-nums">{money(b.vatAmount)} lei</span>
              </div>
            ))}
            <div className="mt-2 flex justify-between border-t border-gray-200 pt-2 text-base font-semibold text-gray-900">
              <span>Total de plată</span>
              <span className="tabular-nums">{money(totals.totalAmount)} lei</span>
            </div>
          </div>
        </div>
      </section>

      {canIssue && editable && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy}
              className="rounded border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50 disabled:opacity-50"
            >
              {create.isPending || update.isPending ? 'Se salvează…' : 'Salvează ciorna'}
            </button>
            <button
              type="button"
              onClick={() => void handleValidate()}
              disabled={busy || validate.isPending}
              title={validation ? 'Validează din nou la ANAF' : undefined}
              className={`inline-flex items-center gap-1.5 rounded border px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                validation === 'ok' && !validate.isPending
                  ? 'border-green-300 bg-green-50 text-green-800 hover:bg-green-100'
                  : validation === 'failed' && !validate.isPending
                    ? 'border-red-300 bg-red-50 text-red-800 hover:bg-red-100'
                    : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50'
              }`}
            >
              {validate.isPending ? (
                'Se validează…'
              ) : validation === 'ok' ? (
                <>
                  <CheckCircle2 size={16} /> Validată de ANAF
                </>
              ) : validation === 'failed' ? (
                <>
                  <XCircle size={16} /> ANAF a găsit probleme
                </>
              ) : (
                'Validează la ANAF'
              )}
            </button>
            {invoice && invoice.status === 'DRAFT' && (
              <button
                type="button"
                onClick={() => void handleCancel()}
                disabled={busy}
                className="rounded px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Anulează ciorna
              </button>
            )}
          </div>
          {canApprove ? (
            <button
              type="button"
              onClick={() => void handleApprove()}
              disabled={busy}
              className="inline-flex items-center gap-2 rounded bg-green-600 px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
              {approve.isPending
                ? 'Se trimite în SPV…'
                : invoice && invoice.status !== 'DRAFT'
                  ? 'Aprobă și retrimite în SPV'
                  : 'Aprobă și trimite în SPV'}
            </button>
          ) : (
            <span className="text-xs text-gray-500">
              Aprobarea și trimiterea în SPV le face un utilizator cu drept de aprobare.
            </span>
          )}
        </div>
      )}
      {showSeries && (
        <InvoiceSeriesModal
          companyId={companyId ?? undefined}
          onClose={() => setShowSeries(false)}
        />
      )}
    </div>
  );
}
